import { Anchor, Button, Group, Modal, Stack, Table, Text, TextInput, Textarea } from "@mantine/core";
import { ConfirmNote, i18n, nextStepToast, useHelpContext } from "@dehub/theme";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { ReturnApi } from "../../../apis";
import type { IReturnSummary } from "../../../apis/return";
import { DotPill, TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { DetailCard } from "../../../components/return/detail-card";
import { ReturnKPICards } from "../../../components/return/kpi-cards";
import { ReturnListTable } from "../../../components/return/list-table";
import { BE_MAX_DISPUTES } from "../../../components/return/return-actions";
import { message } from "../../../utils/message";
import { dateFormat } from "../../../utils/date";

// Larger cohort fetched once for the "limit reached" KPI so the count is not
// scoped to a single 20-row page.
const KPI_COHORT_LIMIT = 100; // list.ts caps limit at 100 (400 above)

const MIN_REASON = 3;

const breadcrumb = () => [
  { to: "/", label: i18n.t("returns:breadcrumb.home") },
  { to: "/returns", label: i18n.t("returns:breadcrumb.returns") },
  { label: i18n.t("returns:disputesPage.breadcrumb") }
];

export function DisputesListPage() {
  const { t } = useTranslation("returns");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const params = {
    status: "DISPUTED" as const,
    page,
    limit: 20,
    sortBy: "requestedAt" as const,
    sortDir: "desc" as const,
    returnNumberPrefix: search || undefined
  };

  const { data, isLoading, mutate } = useSWR(
    ["returns.disputes", page, search],
    () => ReturnApi.list(params),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  // Generic "Manager-руу" escalate prompt (sets escalation flag). The row action itself reads the
  // permission matrix (RETURN_ESCALATE): without it the action stays visible, aria-disabled.
  const [escTarget, setEscTarget] = useState<IReturnSummary | null>(null);
  const [escReason, setEscReason] = useState("");
  const [escBusy, setEscBusy] = useState(false);
  const [escOpen, setEscOpen] = useState(false);

  // Larger DISPUTED cohort (search-aware) for the "limit reached" KPI.
  const { data: kpiData, mutate: mutateKpi } = useSWR(
    ["returns.disputes.kpi", search],
    () => ReturnApi.list({
      status: "DISPUTED" as const,
      page: 1,
      limit: KPI_COHORT_LIMIT,
      sortBy: "requestedAt" as const,
      sortDir: "desc" as const,
      returnNumberPrefix: search || undefined
    }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const disputeMax = BE_MAX_DISPUTES;
  const total = data?.pagination.total ?? 0;
  const limitReached = (kpiData?.returns ?? []).filter((r) => r.disputeCount >= disputeMax).length;

  // §9 ESCALATED QUEUE — the list endpoint can't filter by escalatedAt, so we
  // client-filter the DISPUTED cohort. Generic escalations on non-DISPUTED
  // statuses are not captured here (follow-up: a server-side escalated filter).
  const escalatedRows = (kpiData?.returns ?? []).filter((r) => r.escalatedAt != null);

  const refresh = () => { mutate(); mutateKpi(); };

  // @dehub/help: counts drive the drawer's «Одоо» cards (limit reached → manager decides).
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ vars: { disputedCount: total, limitCount: limitReached } });
  }, [total, limitReached, setHelpContext]);

  const openEscalate = (r: IReturnSummary) => { setEscTarget(r); setEscReason(""); setEscOpen(true); };

  const submitEscalate = async () => {
    if (!escTarget || escReason.trim().length < MIN_REASON) return;
    try {
      setEscBusy(true);
      await ReturnApi.escalate(escTarget.returnRequestId, { reason: escReason.trim(), expectedVersion: escTarget.version });
      nextStepToast("order.returns-detail.toast.escalate", { fallback: { title: t("toast.success"), message: t("actionBar.toast.escalated") } });
      setEscOpen(false);
      refresh();
    } catch (e) {
      // O200 — 409 error.return.already_escalated is translated by key; the queue is reloaded so the row shows the flag.
      message.error((e as Error).message);
      refresh();
    } finally {
      setEscBusy(false);
    }
  };

  return (
    <PageLayout
      title={t("disputesPage.title")}
      subTitle={t("disputesPage.subtitle")}
      breadcrumb={breadcrumb()}
      loading={isLoading && !data}
    >
      <Stack gap="md">
        <ReturnKPICards
          kpis={[
            { label: t("disputesPage.activeDisputes"), value: total, hint: t("disputesPage.disputedTotal"), tone: "amber" },
            { label: t("disputesPage.limitReached"), value: limitReached, hint: t("disputesPage.limitHint", { max: disputeMax }), tone: "red" },
            { label: t("disputesPage.escalatedKpi"), value: escalatedRows.length, hint: t("disputesPage.managerQueue"), tone: "grey" }
          ]}
        />
        <Group>
          <TextInput
            placeholder={t("disputesPage.searchPlaceholder")}
            aria-label={t("disputesPage.searchPlaceholder")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
            w={280}
          />
        </Group>

        <ReturnListTable
          rows={data?.returns ?? []}
          page={data?.pagination.page ?? page}
          totalPages={data?.pagination.totalPages ?? 1}
          total={data?.pagination.total}
          limit={data?.pagination.limit}
          onPageChange={setPage}
          loading={isLoading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          selectable={false}
          showDispute
          disputeMax={disputeMax}
          onManagerEscalate={openEscalate}
          emptyHelpKey="order.returns-disputes.empty.main"
          emptyWhen={search ? "noResults" : null}
          emptyTitle={t("disputesPage.empty")}
          emptyDescription={t("disputesPage.emptyHint")}
        />

        {/* §9 ESCALATED QUEUE (manager) */}
        <DetailCard title={t("disputesPage.escalatedQueue")} helpKey="order.returns-disputes.section.escalated-queue">
          {escalatedRows.length === 0 ? (
            <Text size="sm" c="var(--nw-text-2)">{t("disputesPage.noEscalated")}</Text>
          ) : (
            <TableFrame>
              <Table.ScrollContainer minWidth={720}>
                <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("disputesPage.col.retNumber")}</Table.Th>
                      <Table.Th>{t("disputesPage.col.buyer")}</Table.Th>
                      <Table.Th>{t("disputesPage.col.trust")}</Table.Th>
                      <Table.Th>{t("disputesPage.col.dispute")}</Table.Th>
                      <Table.Th>{t("disputesPage.col.escalated")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {escalatedRows.map((r) => (
                      <Table.Tr key={r.returnRequestId}>
                        <Table.Td>
                          <Anchor component={Link} to={`/returns/${r.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no">{r.returnNumber}</Anchor>
                        </Table.Td>
                        <Table.Td><Text size="sm">{r.buyerBusinessName || "-"}</Text></Table.Td>
                        <Table.Td>
                          {r.trustTier ? (
                            <DotPill tone="grey">{t(`trustLevel.${r.trustTier}`, { defaultValue: "-" })} {r.trustScore != null ? Math.round(r.trustScore) : ""}</DotPill>
                          ) : <Text size="sm">-</Text>}
                        </Table.Td>
                        <Table.Td><Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{r.disputeCount} / {disputeMax}</Text></Table.Td>
                        <Table.Td><Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{dateFormat(r.escalatedAt)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </TableFrame>
          )}
        </DetailCard>
      </Stack>

      {/* Generic escalate-to-manager prompt: confirm modal 440 */}
      <Modal opened={escOpen} onClose={() => setEscOpen(false)} title={t("actionBar.escalateTitle")} size={440}>
        <Stack gap="md">
          {escTarget ? <Text size="sm" c="var(--nw-text-2)" ff="monospace" translate="no">{escTarget.returnNumber}</Text> : null}
          <Textarea
            label={t("transitResolve.reason")}
            required
            value={escReason}
            onChange={(e) => setEscReason(e.currentTarget.value)}
            error={escReason.length > 0 && escReason.trim().length < MIN_REASON ? t("transitResolve.min3") : undefined}
            autosize minRows={2}
          />
          <ConfirmNote helpKey="order.returns-detail.confirm.escalate" mt={0} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEscOpen(false)}>{t("resolutionPanel.cancel")}</Button>
            <Button onClick={submitEscalate} loading={escBusy} disabled={escReason.trim().length < MIN_REASON}>
              {t("actionBar.forward")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageLayout>
  );
}
